import React, { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Lang } from "@/components/nutrimart/types";

interface PendingTx {
  id: string;
  order_id: string;
  user_id: string;
  amount: number;
  currency: string;
  provider: string;
  status: string;
  provider_ref: string | null;
  receipt_url: string | null;
  created_at: string;
}

/**
 * Cola "Pagos por verificar" — admin marca como `captured` (aprobar) o
 * `failed` (rechazar). El trigger sobre `orders` ya envía email +
 * descuenta stock cuando la orden pasa a `paid`.
 */
export function AdminPaymentsQueue({ lang }: { lang: Lang }) {
  const [rows, setRows] = useState<PendingTx[]>([]);
  const [loading, setLoading] = useState(true);
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("payment_transactions")
      .select("id, order_id, user_id, amount, currency, provider, status, provider_ref, receipt_url, created_at")
      .eq("status", "pending")
      .in("provider", ["manual_transfer", "cod"])
      .order("created_at", { ascending: false })
      .limit(50);
    if (error) {
      console.error("queue read error", error);
    } else if (data) {
      setRows(data as PendingTx[]);
      const urls: Record<string, string> = {};
      await Promise.all(
        data
          .filter((r) => r.receipt_url)
          .map(async (r) => {
            const { data: sig } = await supabase.storage
              .from("payment-receipts")
              .createSignedUrl(r.receipt_url!, 600);
            if (sig?.signedUrl) urls[r.id] = sig.signedUrl;
          }),
      );
      setSignedUrls(urls);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function decide(tx: PendingTx, approve: boolean) {
    let reason: string | null = null;
    if (!approve) {
      const input = window.prompt(
        lang === "es"
          ? "Motivo del rechazo (opcional, se enviará al cliente):"
          : "Rejection reason (optional, will be emailed to the customer):",
        "",
      );
      // null = admin cancelled the prompt; "" = submitted blank → also abort
      if (input === null) return;
      reason = input.trim() || null;
    }
    setBusy(tx.id);
    try {
      const newStatus = approve ? "captured" : "failed";
      const { error: txErr } = await supabase
        .from("payment_transactions")
        .update({
          status: newStatus,
          ...(reason ? { error_message: reason } : {}),
        })
        .eq("id", tx.id);
      if (txErr) throw txErr;
      const { error: ordErr } = await supabase
        .from("orders")
        .update({
          payment_status: approve ? "paid" : "failed",
          status: approve ? "paid" : "payment_rejected",
        })
        .eq("id", tx.order_id);
      if (ordErr) throw ordErr;

      // Audit trail — who decided what, when, and why.
      // Fire-and-forget: never block the admin if the log write fails.
      try {
        await supabase.rpc("log_admin_action", {
          _action: approve ? "payment_approve" : "payment_reject",
          _target_user_id: tx.user_id,
          _params: {
            transaction_id: tx.id,
            order_id: tx.order_id,
            provider: tx.provider,
            provider_ref: tx.provider_ref,
            amount: Number(tx.amount),
            currency: tx.currency,
            reason: reason ?? null,
          },
          _result_summary: {
            payment_transaction_status: newStatus,
            order_status: approve ? "paid" : "payment_rejected",
            payment_status: approve ? "paid" : "failed",
          },
        });
      } catch (auditErr) {
        console.warn("payment audit log failed", auditErr);
      }

      // Notify the customer (fire-and-forget — never block the admin flow).
      try {
        const { data: profile } = await supabase
          .from("profiles")
          .select("email, first_name, last_name")
          .eq("user_id", tx.user_id)
          .maybeSingle();
        const customerEmail = profile?.email ?? null;
        if (customerEmail) {
          const customerName =
            [profile?.first_name, profile?.last_name].filter(Boolean).join(" ").trim() || undefined;
          const origin = typeof window !== "undefined" ? window.location.origin : "";
          const templateName = approve ? "client-payment-approved" : "client-payment-rejected";
          await supabase.functions.invoke("send-transactional-email", {
            body: {
              templateName,
              recipientEmail: customerEmail,
              idempotencyKey: `client-${approve ? "approved" : "rejected"}-${tx.id}`,
              templateData: {
                customerName,
                orderId: tx.order_id.slice(0, 8),
                reference: tx.provider_ref ?? undefined,
                amount: Number(tx.amount),
                ...(approve
                  ? { orderUrl: `${origin}/?page=orders` }
                  : {
                      supportUrl: `${origin}/?page=contact`,
                      ...(reason ? { reason } : {}),
                    }),
              },
            },
          });
        }
      } catch (notifyErr) {
        console.warn("client payment email failed", notifyErr);
      }

      setRows((p) => p.filter((r) => r.id !== tx.id));
    } catch (e) {
      console.error("approval error", e);
      alert(lang === "es" ? "Error al actualizar" : "Update failed");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div data-testid="admin-payments-queue">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h3 style={{ margin: 0 }}>
          {lang === "es" ? "Pagos por verificar" : "Payments to verify"} ({rows.length})
        </h3>
        <button className="btn bs" type="button" onClick={refresh} disabled={loading}>
          {lang === "es" ? "Refrescar" : "Refresh"}
        </button>
      </div>

      {loading && <p style={{ color: "var(--tx3)" }}>{lang === "es" ? "Cargando..." : "Loading..."}</p>}

      {!loading && rows.length === 0 && (
        <p style={{ color: "var(--tx3)", padding: 20, textAlign: "center" }}>
          ✓ {lang === "es" ? "Sin pagos pendientes" : "No pending payments"}
        </p>
      )}

      {rows.map((tx) => (
        <div
          key={tx.id}
          style={{
            border: "1px solid var(--brd)",
            borderRadius: "var(--r2)",
            padding: 12,
            marginTop: 10,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div>
              <div style={{ fontFamily: "monospace", fontSize: 12 }}>
                {tx.provider_ref ?? tx.order_id.slice(0, 8)}
              </div>
              <div style={{ fontSize: 11, color: "var(--tx3)" }}>
                {tx.provider} · {new Date(tx.created_at).toLocaleString()}
              </div>
            </div>
            <div style={{ fontWeight: 700 }}>
              RD${Number(tx.amount).toLocaleString()}
            </div>
          </div>
          {tx.receipt_url ? (
            signedUrls[tx.id] ? (
              <a
                href={signedUrls[tx.id]}
                target="_blank"
                rel="noreferrer"
                style={{ fontSize: 12, display: "inline-block", marginTop: 6 }}
              >
                📎 {lang === "es" ? "Ver comprobante" : "View receipt"}
              </a>
            ) : (
              <span style={{ fontSize: 11, color: "var(--tx3)" }}>
                {lang === "es" ? "Generando link..." : "Generating link..."}
              </span>
            )
          ) : (
            <span style={{ fontSize: 11, color: "var(--tx3)" }}>
              ⏳ {lang === "es" ? "Sin comprobante aún" : "No receipt yet"}
            </span>
          )}
          <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
            <button
              className="btn bp bs"
              type="button"
              disabled={busy === tx.id}
              onClick={() => decide(tx, true)}
            >
              ✓ {lang === "es" ? "Aprobar" : "Approve"}
            </button>
            <button
              className="btn bo bs"
              type="button"
              disabled={busy === tx.id}
              onClick={() => decide(tx, false)}
            >
              ✗ {lang === "es" ? "Rechazar" : "Reject"}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}